import React, { useState } from "react";
import PropTypes from "prop-types";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import BaseDataEntryDialog from "./BaseDataEntryDialog.jsx";
import DialogTextField from "components/common/inputs/DialogTextField.js";
import FieldLabel from "components/common/inputs/FieldLabel.jsx";
import { apiBase } from "functions/server/apiBase";
import JobApplicationRequest from "../../../open-api-gen/model/JobApplicationRequest";


const SubmitResumeDialog = ({ open, handleClose, jobTitle }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    coverLetter: "",
  });
  const [resume, setResume] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const abortControllerRef = React.useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError("File size exceeds 5MB limit.");
        return;
      }
      setResume(file);
      setResumeName(file.name);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !resume) {
      setError("Please fill in all required fields and upload your resume.");
      return;
    }

    setLoading(true);
    setError(null);
    abortControllerRef.current = new AbortController();

    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const resumeBase64 = reader.result.split(',')[1];

          const applicationRequest = new JobApplicationRequest(
            formData.name,
            formData.email,
            resumeBase64,
            resumeName
          );
          applicationRequest.cover_letter = formData.coverLetter;

          const controller = abortControllerRef.current;
          const timeoutId = setTimeout(() => controller?.abort(), 15000);

          const response = await fetch(apiBase() + '/job_application', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(applicationRequest),
            signal: controller.signal,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Server responded with status ${response.status}`);
          }

          setLoading(false);
          setSuccess(true);
        } catch (err) {
          setLoading(false);
          if (err.name === 'AbortError') {
            setError("Request cancelled. Please try again.");
          } else {
            setError(err.message || "An error occurred while submitting your application.");
          }
        }
      };
      reader.readAsDataURL(resume);
    } catch (err) {
      setLoading(false);
      setError(err.message || "An error occurred while submitting your application.");
    }
  };

  const handleCancel = () => {
    if (loading && abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
      setError("Application submission cancelled.");
    } else {
      handleClose();
    }
  };

  const resetForm = () => {
    setFormData({ name: "", email: "", coverLetter: "" });
    setResume(null);
    setResumeName("");
    setSuccess(false);
    setError(null);
  };

  const onClose = () => {
    if (loading) {
      abortControllerRef.current?.abort();
    }
    handleClose();
    if (success) {
      setTimeout(resetForm, 300);
    } else {
      resetForm();
    }
  };

  return (
    <BaseDataEntryDialog
      open={open}
      onClose={onClose}
      title={success ? "Application Sent!" : `Apply for ${jobTitle}`}
      description={
        success
          ? undefined
          : "Please provide your details and upload your resume. Our team will review your application and get back to you."
      }
      maxWidth={success ? "xs" : "sm"}
      titleId="submit-resume-dialog-title"
      descriptionId="submit-resume-dialog-description"
      actions={
        success ? (
          <Button
            variant="contained"
            onClick={onClose}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 2 }}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              onClick={handleCancel}
              disabled={loading}
              sx={{ color: "text.secondary", fontWeight: 600, textTransform: "none" }}
            >
              {loading ? "Cancelling..." : "Cancel"}
            </Button>
            <Tooltip
              title={
                !formData.name ? "Please enter your full name" :
                !formData.email ? "Please enter your email address" :
                !resume ? "Please upload your resume" : ""
              }
            >
              <span>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  disabled={loading || !formData.name || !formData.email || !resume}
                  sx={{
                    fontWeight: 700,
                    px: 3,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  {loading ? "Submitting..." : "Submit Application"}
                </Button>
              </span>
            </Tooltip>
          </>
        )
      }
    >
      {success ? (
        <Box sx={{ textAlign: "center", py: 3 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 1, color: "dark.main" }}>
            Thank you!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We've received your application for the <strong>{jobTitle}</strong> position
            and will be in touch soon.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative" }}>
          <Stack spacing={2.5}>
            {error && <Alert severity="error">{error}</Alert>}

            <DialogTextField
              label="Full name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              fullWidth
              required
              size="small"
            />

            <DialogTextField
              label="Email address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              fullWidth
              required
              size="small"
            />

            <Box>
              {/* Deliberately not htmlFor: the Button below is already wrapped in
                  a <label for="resume-file-upload"> to trigger the picker, and a
                  second label would concatenate into the input's name. */}
              <FieldLabel required sx={{ mb: 1 }}>
                Resume (PDF or Word)
              </FieldLabel>
              <input
                accept=".pdf,.doc,.docx"
                style={{ display: "none" }}
                id="resume-file-upload"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="resume-file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                  sx={{
                    borderStyle: "dashed",
                    py: 0.75,
                    textTransform: "none",
                    borderColor: resumeName ? "success.main" : "divider",
                    color: resumeName ? "success.main" : "text.secondary",
                    "&:hover": {
                      borderColor: "primary.main",
                      backgroundColor: "primary.extraLight",
                    },
                  }}
                >
                  {resumeName || "Upload Resume"}
                </Button>
              </label>
              {resumeName && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ mt: 0.5, display: "block", fontWeight: 600 }}
                >
                  Selected: {resumeName}
                </Typography>
              )}
            </Box>

            <DialogTextField
              label="Cover letter (optional)"
              name="coverLetter"
              value={formData.coverLetter}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Tell us a bit about why you're a great fit for this role..."
            />
          </Stack>

          {loading && (
            <LinearProgress
              sx={{ position: "absolute", bottom: -20, left: -20, right: -20 }}
            />
          )}
        </Box>
      )}
    </BaseDataEntryDialog>
  );
};

SubmitResumeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  jobTitle: PropTypes.string.isRequired,
};

export default SubmitResumeDialog;
